import { IOpeningHours, IRegularPeriod } from '@pepperhq/types-sdk';
import { DaysOfTheWeek } from './model/Location';
import { format } from 'date-fns';
import { TZDate } from '@date-fns/tz';

export const DAY_NUMBER_TO_DAY: Record<number, DaysOfTheWeek> = {
    0: DaysOfTheWeek.MONDAY,
    1: DaysOfTheWeek.TUESDAY,
    2: DaysOfTheWeek.WEDNESDAY,
    3: DaysOfTheWeek.THURSDAY,
    4: DaysOfTheWeek.FRIDAY,
    5: DaysOfTheWeek.SATURDAY,
    6: DaysOfTheWeek.SUNDAY
};

export const DAY_TO_DAY_NUMBER: Record<DaysOfTheWeek, number> = {
    [DaysOfTheWeek.MONDAY]: 0,
    [DaysOfTheWeek.TUESDAY]: 1,
    [DaysOfTheWeek.WEDNESDAY]: 2,
    [DaysOfTheWeek.THURSDAY]: 3,
    [DaysOfTheWeek.FRIDAY]: 4,
    [DaysOfTheWeek.SATURDAY]: 5,
    [DaysOfTheWeek.SUNDAY]: 6
};

// Fallback timezone when neither the location nor the merchant region specifies one.
export const DEFAULT_TIMEZONE = 'Europe/London';

// Resolves the effective IANA timezone: prefer the location's, then the merchant
// region's, then the London default. Mirrors the chain in auth's getSessionExpiryDate.
export function resolveTimezone(locationTimezone?: string, regionTimezone?: string): string {
    return locationTimezone || regionTimezone || DEFAULT_TIMEZONE;
}

// Minimal time/slot shapes shared by regular and override entries (no day/date field).
type TimeOfDay = { hour: number; minute: number };
type TimeSlot = { openTime: TimeOfDay; closeTime: TimeOfDay };

function toMins(t: TimeOfDay): number {
    return t.hour * 60 + t.minute;
}

// Returns the active slots for a given date, respecting override closed dates and override hours.
export function getActiveSlotsForDate(
    openingHours: IOpeningHours<string>,
    dateStr: string,
    day: DaysOfTheWeek
): TimeSlot[] {
    const isOverrideClosed = openingHours.overrides
        ?.flatMap(o => o.closedDates ?? [])
        .some(d => d === dateStr);
    if (isOverrideClosed) {
        return [];
    }

    const hasOverrideHours =
        openingHours.overrides?.flatMap(o => o.dates ?? []).some(s => s.openTime.date === dateStr) ?? false;
    if (hasOverrideHours) {
        return (
            openingHours.overrides?.flatMap(o => o.dates ?? []).filter(s => s.openTime.date === dateStr) ?? []
        );
    }
    return getDaySlots(openingHours.regular, day);
}

// Extracts slots for a single day from the flat regular array, stripping the
// day field so the existing display helpers can work with them.
export function getDaySlots(regular: IRegularPeriod[], day: DaysOfTheWeek) {
    const dayNumber = DAY_TO_DAY_NUMBER[day];
    return regular
        .filter(s => s.openTime.day === dayNumber)
        .map(s => ({
            openTime: { hour: s.openTime.hour, minute: s.openTime.minute },
            closeTime: { hour: s.closeTime.hour, minute: s.closeTime.minute }
        }));
}

export function formatTime(value: TimeOfDay): string {
    const date = new Date();
    date.setHours(value.hour, value.minute, 0, 0);
    return format(date, 'h:mm a');
}

// 00:00 is midnight of the current day (not overnight), so only flag as overnight
// when closeTime is non-zero and falls before openTime in wall-clock minutes.
export function isOvernight(range: TimeSlot): boolean {
    if (!range.openTime || !range.closeTime) {
        return false;
    }
    if (range.closeTime.hour === 0 && range.closeTime.minute === 0) {
        return false;
    }
    return (
        range.closeTime.hour * 60 + range.closeTime.minute < range.openTime.hour * 60 + range.openTime.minute
    );
}

export function getTodaySlots(
    openingHours: IOpeningHours<string>,
    timezone: string
): {
    isClosed: boolean;
    formattedSlots: string[];
} {
    // Resolve "today" against the venue's wall clock, not the browser's.
    const today = TZDate.tz(timezone);
    const dayDateStr = format(today, 'yyyy-MM-dd');
    const day = DAY_NUMBER_TO_DAY[(today.getDay() + 6) % 7];
    const activeSlots = getActiveSlotsForDate(openingHours, dayDateStr, day);

    return {
        isClosed: activeSlots.length === 0,
        formattedSlots: activeSlots.map(slot => formatRange(slot))
    };
}

// Returns the open/closed state and a single-line summary label for the location card.
// Handles overnight slots by checking yesterday's schedule for any slot still active past midnight.
export function getWorkingHoursSummary(
    openingHours: IOpeningHours<string>,
    timezone: string
): { isOpenNow: boolean; label: string } {
    // Anchor "now" and all derived dates to the location's timezone so open/close
    // detection reflects the venue's wall clock, not the browser's.
    const now = TZDate.tz(timezone);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const todayDateStr = format(now, 'yyyy-MM-dd');
    const todayDay = DAY_NUMBER_TO_DAY[(now.getDay() + 6) % 7];

    const yesterday = new TZDate(now, timezone);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayDateStr = format(yesterday, 'yyyy-MM-dd');
    const yesterdayDay = DAY_NUMBER_TO_DAY[(yesterday.getDay() + 6) % 7];

    // Check yesterday's overnight slots still active past midnight
    for (const slot of getActiveSlotsForDate(openingHours, yesterdayDateStr, yesterdayDay)) {
        const closeMins = toMins(slot.closeTime);
        if (isOvernight(slot) && closeMins !== 0 && nowMins < closeMins) {
            return { isOpenNow: true, label: `Open · Closes ${formatTime(slot.closeTime)}` };
        }
    }

    // Check today's slots in time order
    const todaySlots = getActiveSlotsForDate(openingHours, todayDateStr, todayDay).sort(
        (a, b) => toMins(a.openTime) - toMins(b.openTime)
    );

    for (const slot of todaySlots) {
        const openMins = toMins(slot.openTime);
        const closeMins = toMins(slot.closeTime);
        // 00:00 close means midnight (end of day), treat as open until end of day
        const effectiveClose = closeMins === 0 ? 24 * 60 : closeMins;
        const overnight = isOvernight(slot);

        if (overnight ? nowMins >= openMins : nowMins >= openMins && nowMins < effectiveClose) {
            return { isOpenNow: true, label: `Open · Closes ${formatTime(slot.closeTime)}` };
        }
    }

    // Not open now — find the next opening time
    const nextSlotToday = todaySlots.find(s => toMins(s.openTime) > nowMins);
    if (nextSlotToday) {
        return { isOpenNow: false, label: `Closed · Opens ${formatTime(nextSlotToday.openTime)}` };
    }

    for (let i = 1; i <= 7; i++) {
        const futureDate = new TZDate(now, timezone);
        futureDate.setDate(futureDate.getDate() + i);
        const futureDateStr = format(futureDate, 'yyyy-MM-dd');
        const futureDay = DAY_NUMBER_TO_DAY[(futureDate.getDay() + 6) % 7];
        const futureSlots = getActiveSlotsForDate(openingHours, futureDateStr, futureDay).sort(
            (a, b) => toMins(a.openTime) - toMins(b.openTime)
        );

        if (futureSlots.length > 0) {
            return {
                isOpenNow: false,
                label: `Closed · Opens ${format(futureDate, 'EEE')} ${formatTime(futureSlots[0].openTime)}`
            };
        }
    }

    return { isOpenNow: false, label: 'Closed' };
}

// Returns the Date at which the currently-open slot closes, or undefined when closed now.
// Mirrors the open-detection in getWorkingHoursSummary and builds a concrete Date from the
// active slot's closeTime, rolling into the next day for overnight / midnight-close slots.
export function getCurrentClosingTime(
    openingHours: IOpeningHours<string>,
    timezone: string
): Date | undefined {
    // Anchor "now" and all derived dates to the location's timezone so the open/close
    // detection and the returned closing instant reflect the venue's wall clock, not the
    // browser's. TZDate's accessors/setters and date-fns format() all operate in `timezone`.
    const now = TZDate.tz(timezone);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const todayDateStr = format(now, 'yyyy-MM-dd');
    const todayDay = DAY_NUMBER_TO_DAY[(now.getDay() + 6) % 7];

    const yesterday = new TZDate(now, timezone);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayDateStr = format(yesterday, 'yyyy-MM-dd');
    const yesterdayDay = DAY_NUMBER_TO_DAY[(yesterday.getDay() + 6) % 7];

    const closeAt = (closeTime: { hour: number; minute: number }, nextDay: boolean): Date => {
        const close = new TZDate(now, timezone);
        close.setHours(closeTime.hour, closeTime.minute, 0, 0);
        if (nextDay) {
            close.setDate(close.getDate() + 1);
        }
        return close;
    };

    // Yesterday's overnight slot still active past midnight closes later today.
    for (const slot of getActiveSlotsForDate(openingHours, yesterdayDateStr, yesterdayDay)) {
        const closeMins = toMins(slot.closeTime);
        if (isOvernight(slot) && closeMins !== 0 && nowMins < closeMins) {
            return closeAt(slot.closeTime, false);
        }
    }

    // Today's slots in time order.
    const todaySlots = getActiveSlotsForDate(openingHours, todayDateStr, todayDay).sort(
        (a, b) => toMins(a.openTime) - toMins(b.openTime)
    );
    for (const slot of todaySlots) {
        const openMins = toMins(slot.openTime);
        const closeMins = toMins(slot.closeTime);
        const effectiveClose = closeMins === 0 ? 24 * 60 : closeMins;
        const overnight = isOvernight(slot);
        if (overnight ? nowMins >= openMins : nowMins >= openMins && nowMins < effectiveClose) {
            // Overnight slots and 00:00 (end-of-day) closes roll into the next day.
            return closeAt(slot.closeTime, overnight || closeMins === 0);
        }
    }

    return undefined;
}

export function formatRange(range: TimeSlot): string {
    const from = range.openTime ? formatTime(range.openTime) : '';
    const to = range.closeTime ? formatTime(range.closeTime) : '';
    const overnight = isOvernight(range);
    if (from && to) {
        return `${from} - ${to}${overnight ? ' (next day)' : ''}`;
    }
    if (from) {
        return `Open from ${from}`;
    }
    if (to) {
        return `Open until ${to}`;
    }
    return '';
}
